import React from 'react';
import Box from '@mui/material/Box';
import {LinearProgress, Paper, Typography} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const spinSx = {
    fontSize: 16,
    animation: 'spin 1.5s linear infinite',
    '@keyframes spin': {from: {transform: 'rotate(0deg)'}, to: {transform: 'rotate(360deg)'}},
};

const getStatus = v => {
    if (v === -1) return 'done';
    if (v > 0) return 'inProgress';
    return 'pending';
};

const statusOrder = {done: 0, inProgress: 1, pending: 2};

/**
 * Panneau de progression affiché pendant le chargement paginé des sources.
 * Ordre d'affichage : terminé → en cours → pas encore commencé.
 * @param {{ sources: Array<{label, value, color}>, codeProgress: {idx, total}|null }} props
 */
export default function LoadingPanel({sources, codeProgress}) {
    const sorted = [...sources].sort((a, b) =>
        statusOrder[getStatus(a.value)] - statusOrder[getStatus(b.value)]
    );

    return (
        <Box sx={{mt: 3, mx: 2}}>
            {/* ── Progression multi-codes ───────────────────────────────────── */}
            {codeProgress && (
                <Paper elevation={0} sx={{
                    px: 2, py: 1.5, borderRadius: 2,
                    border: '1px solid', borderColor: 'divider',
                    display: 'flex', flexDirection: 'column', gap: 0.8,
                    width: '50%', mx: 'auto', mb: 1,
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <HourglassEmptyIcon sx={{...spinSx, color: 'primary.main'}}/>
                        <Typography variant="body2" fontWeight="medium">
                            Chargement ({codeProgress.code})
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ml: 'auto'}}>
                            {codeProgress.idx} / {codeProgress.total}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={(codeProgress.idx / codeProgress.total) * 100}
                        sx={{
                            borderRadius: 4, height: 5,
                            bgcolor: 'rgba(25,118,210,0.15)',
                            '& .MuiLinearProgress-bar': {bgcolor: 'primary.main'},
                        }}
                    />
                </Paper>
            )}

            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                {sorted.map(({label, value, color, count}) => {
                    const status = getStatus(value);
                    return (
                        <Paper key={label} elevation={0} sx={{
                            px: 2, py: 1.5, borderRadius: 2,
                            border: '1px solid', borderColor: 'divider',
                            display: 'flex', flexDirection: 'column', gap: 0.8,
                            opacity: status === 'pending' ? 0.5 : 1,
                            width: '50%',
                            mx: 'auto',
                        }}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                {status === 'done' && (
                                    <CheckCircleIcon sx={{fontSize: 16, color: 'success.main'}}/>
                                )}
                                {status === 'inProgress' && (
                                    <HourglassEmptyIcon sx={{...spinSx, color: 'warning.main'}}/>
                                )}
                                {status === 'pending' && (
                                    <RadioButtonUncheckedIcon sx={{fontSize: 16, color: 'text.disabled'}}/>
                                )}
                                <Typography variant="body2" fontWeight="medium">{label}</Typography>
                                <Box sx={{ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5}}>
                                    {count > 0 && (
                                        <Typography variant="caption" sx={{
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                            color: status === 'done' ? 'success.main' : 'text.secondary',
                                            bgcolor: status === 'done' ? 'rgba(46,125,50,0.08)' : 'rgba(0,0,0,0.05)',
                                            px: 0.8, py: 0.1, borderRadius: 1,
                                        }}>
                                            {count.toLocaleString('fr-FR')}&nbsp;enr.
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {status === 'done' && 'terminé'}
                                        {status === 'inProgress' && `lot ${value}`}
                                        {status === 'pending' && 'en attente'}
                                    </Typography>
                                </Box>
                            </Box>
                            <LinearProgress
                                variant={status === 'done' ? 'determinate' : status === 'inProgress' ? 'indeterminate' : 'determinate'}
                                value={status === 'done' ? 100 : 0}
                                sx={{
                                    borderRadius: 4, height: 5,
                                    bgcolor: `${color}22`,
                                    '& .MuiLinearProgress-bar': {bgcolor: color},
                                }}
                            />
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
}
